import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
  Text,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

import { getLocalSuggestions } from "apis";
import { Suggestion } from "utils/types";
import { generateSuggestionKey } from "utils/functions";

interface SearchInputProps {
  defaultValue?: string;
  onSubmit: (e: Suggestion) => void;
  loading?: boolean;
}

const AutoSuggestInput = ({
  defaultValue = "",
  onSubmit,
  loading = false,
}: SearchInputProps) => {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const initialFocusRef = React.useRef<any>();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [val, setVal] = useState<string>(defaultValue);
  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      const requestData =
        suggestions[0]?.word === val
          ? {
              word: suggestions[0]?.word || val,
              pos: suggestions[0]?.pos || "",
            }
          : {
              word: val,
              pos: "",
            };
      onSubmit(requestData);
    }
  };

  useEffect(() => {
    setVal(defaultValue);
  }, [defaultValue]);

  const fetchSuggestions = useCallback(
    async (value: string) => {
      if (value !== keyword) {
        onOpen();
      } else {
        onClose();
      }

      const response = await getLocalSuggestions(value);
      setSuggestions(response?.data || []);
    },
    [keyword, onClose, onOpen]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVal(value?.toLowerCase());
    if (!value) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    if (!val) {
      return;
    }

    const timerId = setTimeout(() => {
      fetchSuggestions(val);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [fetchSuggestions, val]);

  return (
    <Popover
      initialFocusRef={initialFocusRef}
      isOpen={isOpen && val?.length > 0}
      onClose={onClose}
      defaultIsOpen={false}
    >
      <PopoverTrigger>
        <InputGroup onClick={onOpen}>
          <Input
            ref={initialFocusRef}
            type="text"
            placeholder="Search"
            value={val}
            focusBorderColor="second"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <InputRightElement>
            <IconButton
              variant="ghost"
              onClick={() => onSubmit({ word: val, pos: "" })}
              aria-label="Search word"
              isLoading={loading}
              icon={<SearchIcon color="primary" />}
              _hover={{ bg: "transparent", transform: "scale(1.1)" }}
            />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>
      {!!suggestions?.length && (
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody>
            {suggestions.map((suggestion) => (
              <Box
                key={generateSuggestionKey(suggestion)}
                color="grayTxt"
                fontSize="sm"
                mb="0.25rem"
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={() => onSubmit(suggestion)}
                display="flex"
              >
                <Text fontSize="sm">{suggestion.word}</Text>
                {suggestion.pos && (
                  <Text ml="0.1rem" fontSize="0.6rem">
                    {suggestion.pos}
                  </Text>
                )}
              </Box>
            ))}
          </PopoverBody>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default AutoSuggestInput;
